import { faAdd, faArrowLeft, faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { TextInput, TouchableOpacity, View, Image, ScrollView, DimensionValue, Modal } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import FileUploader from '../../../components/FileUploader';
import { appAgentService } from '../../../services/central-api/app-agent';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import imageCompression from 'browser-image-compression';
import { getFileTypeFromBuffer } from '../../../utils/getFileTypeFromBuffer';

const acceptedMimeTypes = ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'video/mp4', 'image/jpeg', 'image/png'];

interface OccurrenceDetailParams {
    occurrence: Occurrence;
    policeReports: OccurrenceReport[];
    setStep: React.Dispatch<React.SetStateAction<'occurrenceDetail' | 'occurrenceReport' | 'occurrenceInvolved' | 'displaceQuestionary' | 'policeReport'>>;
    getOccurrence: (occurrenceId: number) => Promise<void>;
    shouldFinishOccurrence: boolean;
}

export default function AgentOccurrencePoliceReport({ occurrence, policeReports, setStep, getOccurrence, shouldFinishOccurrence }: OccurrenceDetailParams) {

    const { styles, theme } = useStyles(styleSheet);

    const [policeReportNumber, setPoliceReportNumber] = useState({ dirty: false, value: occurrence.policeReport || '' });
    const [files, setFiles] = useState<{ file: File; objectURI: string; key: string; }[]>([]);
    const [isScreenVisible, setIsScreenVisible] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    async function submit() {
        setIsLoading(true);
        try {
            if (!occurrence.id) {
                return;
            }

            await appAgentService.attachPoliceReport({
                occurrenceId: occurrence.id,
                files: files.map(f => f.file),
                policeReport: policeReportNumber.value,
            });

            await getOccurrence(occurrence.id);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setIsScreenVisible(false);
            if (shouldFinishOccurrence) {
                setStep('occurrenceReport');
            } else {
                setStep('occurrenceDetail');
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        let file = e.target.files[0];

        const fileAlreadySelected = files.find(f => f.file.name === file.name && f.file.lastModified === file.lastModified);

        if (fileAlreadySelected) {
            return;
        }

        if (!file.type) {
            const mime = await getFileTypeFromBuffer(await file.arrayBuffer(), acceptedMimeTypes);

            if (!mime) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate('appAgentFormatNotValid'),
                });
                return;
            }

            file = new File([file], file.name, { type: mime });
        }

        if (!acceptedMimeTypes.includes(file.type)) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('appAgentFormatNotValid'),
            });
            return;
        }

        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            setFiles(current => [{ file, objectURI: URL.createObjectURL(file), key: String(Math.random()) }, ...current]);
            return;
        }

        const compressedFile = await imageCompression(file, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
        });

        setFiles(current => [{ file: compressedFile, objectURI: URL.createObjectURL(compressedFile), key: String(Math.random()) }, ...current]);
    };

    return <Modal
        visible={isScreenVisible}
        onRequestClose={() => {
            setIsScreenVisible(false);
        }}
        transparent={true}
    >
        <View style={[styles.container, { marginTop: 64, backgroundColor: theme.colors.paginationBar.background }]} >
            <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <TouchableOpacity onPress={async () => {
                    setIsScreenVisible(false);
                    setStep('occurrenceDetail');
                }}>
                    <FontAwesomeIcon fontSize={30} icon={faArrowLeft} color={theme.colors.textColor} />
                </TouchableOpacity>
            </View>

            <View style={{ gap: 5 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <MyAppText style={styles.label}>
                        {translate('policeReport')}
                    </MyAppText>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TextInput
                        placeholder={translate('policeReport')}
                        style={[
                            styles.input,
                            policeReportNumber.value == '' && policeReportNumber.dirty ? styles.invalid : null,
                            { color: policeReportNumber.value ? theme.colors.field.text : theme.colors.placeholder }
                        ]}
                        value={policeReportNumber.value}
                        onBlur={() => {
                            setPoliceReportNumber({ dirty: true, value: policeReportNumber.value });
                        }}
                        onChangeText={(value) => {
                            setPoliceReportNumber({ dirty: true, value });
                        }}
                        maxLength={255}
                    />
                </View>
            </View>
            <ScrollView contentContainerStyle={styles.scrollContent}>
                <View style={styles.blockSize}>
                    <FileUploader
                        icon={faAdd}
                        style={[styles.blockSize, styles.blockStyle, { width: '100%' }]}
                        contentContainerStyle={{ flex: 1 }}
                        textStyle={{
                            fontSize: 30,
                            color: '#181A1B',
                        }}
                        onChange={handleFileInput}
                    />
                </View>
                {files.map((file) =>
                    file.file.type.includes('image') ?

                        <Image
                            key={file.key}
                            style={[styles.blockSize, {
                                overflow: 'hidden',
                            }]}
                            source={{
                                uri: file.objectURI
                            }}
                        /> : <View key={Math.random()} style={[styles.blockSize, {
                            backgroundColor: theme.colors.primaryButton.background,
                            justifyContent: 'center'
                        }]}>
                            <FontAwesomeIcon
                                fontSize={30}
                                icon={faFile}
                                color={theme.colors.primaryButton.text}
                                style={{
                                    justifySelf: 'center',
                                    alignSelf: 'center'
                                }}
                            />
                        </View>
                )}
                {policeReports.map((policeReport) => {
                    const content = JSON.parse(policeReport.content);

                    if (content.type.includes('image')) {
                        return <Image
                            key={'police-report-' + policeReport.id}
                            style={[styles.blockSize, {
                                overflow: 'hidden',
                            }]}
                            source={{
                                uri: content.url
                            }}
                        />;
                    }
                    return <View key={Math.random()} style={[styles.blockSize, {
                        backgroundColor: theme.colors.primaryButton.background,
                        justifyContent: 'center'
                    }]}>
                        <FontAwesomeIcon
                            fontSize={30}
                            icon={faFile}
                            color={theme.colors.primaryButton.text}
                            style={{
                                justifySelf: 'center',
                                alignSelf: 'center'
                            }}
                        />
                    </View>;
                })}
            </ScrollView>

            <CustomButton
                onPress={submit}
                text={translate(!occurrence.policeReport ? 'addPoliceReport' : 'updatePoliceReport').toUpperCase()}
                disabled={policeReportNumber.value == '' || (files.length + policeReports.length) == 0 || isLoading}
            />
        </View>
    </Modal>;
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    label: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.textColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    input: {
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        width: '100%',
        height: 40,
        padding: 10,
        borderRadius: 4,
    },
    invalid: {
        borderColor: 'red',
    },
    scrollContent: {
        rowGap: 20,
        columnGap: 16,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start'
    },
    blockSize: {
        width: 'calc(33.33% - 11px)' as DimensionValue,
        height: 97,
        borderRadius: 8,
    },
    blockStyle: {
        backgroundColor: '#C6C6CE',
    },
}));
